import { useCallback } from 'react';

import {
  useTrackDepletedActionBlocked,
  useTrackDepletedStateViewed,
  useTrackDepletedUpsellClicked,
} from '@air/analytics';
import { useAirModal } from '@air/provider-modal';

import { EnterprisePlanModal } from '~/components/Modals/EnterprisePlanModal';
import { PlansModal } from '~/components/Modals/PlansModal/PlansModal';
import { useCanConsumeMoreCredits } from '~/hooks/useCanConsumeMoreCredits';
import { useCreditAnalyticsParams } from '~/hooks/useCreditAnalyticsParams';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';
import { canManageSubscription } from '~/utils/permissions/workspacePermissions';

export const useWorkspaceAgentCreditsDepleted = () => {
  const { canConsumeMoreCredits } = useCanConsumeMoreCredits();
  const creditAnalyticsParams = useCreditAnalyticsParams();
  const { data: permissions, isFetched: areWorkspacePermissionsFetched } = useCurrentWorkspacePermissionsContext();
  const { data: subscription } = useSubscriptionContext();
  const [showPlansModal] = useAirModal(PlansModal);
  const [showEnterprisePlanModal] = useAirModal(EnterprisePlanModal);
  const { trackDepletedActionBlocked } = useTrackDepletedActionBlocked();
  const { trackDepletedStateViewed } = useTrackDepletedStateViewed();
  const { trackDepletedUpsellClicked } = useTrackDepletedUpsellClicked();

  const isCreditsDepleted = !canConsumeMoreCredits;
  const canShowUpgradeCta = areWorkspacePermissionsFetched && canManageSubscription(permissions);
  const upgradeCtaLabel = 'Upgrade plan';

  const trackComposerDepletedViewed = useCallback(() => {
    if (!creditAnalyticsParams) {
      return;
    }

    trackDepletedStateViewed({
      surface: 'workspace_agent',
      ...creditAnalyticsParams,
    });
  }, [creditAnalyticsParams, trackDepletedStateViewed]);

  const trackBlockedCreditAction = useCallback(() => {
    if (!creditAnalyticsParams) {
      return;
    }

    trackDepletedActionBlocked({
      action: 'workspace_agent',
      ...creditAnalyticsParams,
    });
  }, [creditAnalyticsParams, trackDepletedActionBlocked]);

  const handleUpgradeClick = useCallback(() => {
    if (!canShowUpgradeCta) {
      return;
    }

    if (creditAnalyticsParams) {
      trackDepletedUpsellClicked({
        surface: 'workspace_agent',
        ...creditAnalyticsParams,
      });
    }

    if (subscription?.isOnEnterprisePlan) {
      showEnterprisePlanModal();
    } else {
      showPlansModal({ leadFormTrigger: 'credit_banner' });
    }
  }, [
    canShowUpgradeCta,
    creditAnalyticsParams,
    showEnterprisePlanModal,
    showPlansModal,
    subscription?.isOnEnterprisePlan,
    trackDepletedUpsellClicked,
  ]);

  const blockIfCreditsDepleted = useCallback(
    (whenBlocked?: () => void) => {
      if (!isCreditsDepleted) {
        return false;
      }

      trackBlockedCreditAction();
      whenBlocked?.();

      return true;
    },
    [isCreditsDepleted, trackBlockedCreditAction],
  );

  return {
    blockIfCreditsDepleted,
    canShowUpgradeCta,
    handleUpgradeClick,
    isCreditsDepleted,
    trackComposerDepletedViewed,
    upgradeCtaLabel,
  };
};
